import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  selectAccountModal,
  setMode,
  setPendingEmail,
} from '../accountModalSlice';
import { AuthApiError, forgotConsumerPassword } from '../authApi';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const ERROR_KEYS: Record<string, string> = {
  ordering_not_configured: 'auth.errors.forgotUnavailable',
  restaurant_not_found: 'auth.errors.forgotUnavailable',
  server: 'auth.errors.server',
  network: 'auth.errors.network',
  unknown: 'auth.errors.unknownForgot',
};

export default function ForgotPanel() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { restaurantUuid } = useParams<{ restaurantUuid: string }>();
  const { pendingEmail } = useAppSelector(selectAccountModal);
  const [email, setEmail] = useState(pendingEmail ?? '');
  const [error, setError] = useState<string | undefined>();
  const [banner, setBanner] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(() => dispatch(setMode('login')), 5000);
    return () => clearTimeout(t);
  }, [sent, dispatch]);

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setError(t('auth.validation.emailInvalid'));
      return;
    }
    if (!restaurantUuid) {
      setBanner(t('auth.missingRestaurant'));
      return;
    }
    setError(undefined);
    setBanner(null);
    setSubmitting(true);
    try {
      await forgotConsumerPassword({ email: email.trim(), restaurant_uuid: restaurantUuid });
      // Shown whatever the server found. It answers identically for a known and
      // an unknown address, and repeating that here is what keeps this screen
      // from becoming the membership test the API refuses to be.
      setSent(true);
    } catch (err) {
      const code = err instanceof AuthApiError ? err.code : 'unknown';
      setBanner(t(ERROR_KEYS[code] ?? ERROR_KEYS.unknown));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-4 text-center">
        <h2 className="font-display text-2xl text-ink inline-flex items-center justify-center gap-2">
          <svg viewBox="0 0 24 24" className="w-6 h-6" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </svg>
          {t('auth.emailSentTitle')}
        </h2>
        <p className="text-ink/80">{t('auth.emailSentBody')} <strong>{email}</strong></p>
        <button type="button" onClick={() => dispatch(setMode('login'))}
          className="w-full py-3 bg-ink text-paper font-display rounded-btn shadow-stamp-sm cursor-pointer transition-transform duration-200 hover:-translate-y-[1px] hover:-translate-x-[1px]">
          {t('auth.backToLogin')}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-3">
      {banner && (
        <div className="px-3 py-2 bg-yellow-100 border-2 border-ink text-ink text-sm rounded-btn-sm">
          {banner}
        </div>
      )}
      <div>
        <h2 className="font-display text-xl text-ink">{t('auth.forgotTitle')}</h2>
        <p className="text-xs text-ink/70 mt-0.5">{t('auth.forgotSubtitle')}</p>
      </div>
      <label className="flex flex-col">
        <span className="text-xs font-sans font-semibold text-ink mb-1">{t('auth.emailLabel')}</span>
        <input type="email" value={email}
          onChange={e => { setEmail(e.target.value); dispatch(setPendingEmail(e.target.value)); }}
          className={`w-full px-3 py-2 bg-paper border-2 ${error ? 'border-red-600' : 'border-ink'} rounded-btn-sm text-sm`} />
        {error && <span className="text-xs text-red-600 mt-0.5">{error}</span>}
      </label>
      <button type="submit" disabled={submitting}
        className="w-full py-2.5 bg-ink text-paper font-display rounded-btn shadow-stamp-sm cursor-pointer transition-transform duration-200 hover:-translate-y-[1px] hover:-translate-x-[1px] disabled:opacity-60 disabled:hover:transform-none">
        {submitting ? t('auth.sending') : t('auth.sendLink')}
      </button>
      <button type="button"
        className="w-full bg-transparent border-0 p-0 text-sm text-ink/70 underline cursor-pointer hover:text-ink"
        onClick={() => dispatch(setMode('login'))}>
        {t('auth.backToLogin')}
      </button>
    </form>
  );
}
